'use client';

import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
interface IPage {
  page: number;
  pageSize: number;
  total: number;
  direction?: IDirection;
  pageFrom?: PageFromType;
}
// 从 0 开始计算， 还是从1开始计算
enum PageFromType {
  'FROM_0',
  'FROM_1',
}
type IDirection = 'right' | 'left';

export const usePage = ({
  page = 1,
  pageSize = 4,
  total = 0,
  direction = 'right',
  pageFrom = 0,
}: IPage) => {
  const [pageState, setPageState] = useState({
    page,
    pageSize,
    direction: direction || 'right',
  });
  const totalPage = useMemo(() => {
    if (pageSize <= 0) return 1;
    return total % pageSize === 0
      ? total / pageSize
      : ((total / pageSize) | 0) + 1;
  }, [total, pageSize]);

  const totalRef = useRef(totalPage);
  const pageFromRef = useRef(pageFrom);

  pageFromRef.current = pageFrom | 0;
  totalRef.current = totalPage + pageFromRef.current;

  const onPrev = useCallback(() => {
    setPageState((p) => {
      const _page =
        p.page > pageFromRef.current ? p.page - 1 : pageFromRef.current;
      const _direction = _page !== p.page ? 'left' : p.direction;
      return { pageSize: p.pageSize, page: _page, direction: _direction };
    });
  }, []);

  const onNext = useCallback(() => {
    setPageState((p) => {
      const _page = p.page < totalRef.current - 1 ? p.page + 1 : p.page;
      const _direction = _page !== p.page ? 'right' : p.direction;
      return { pageSize: p.pageSize, page: _page, direction: _direction };
    });
  }, []);

  const onChange = useCallback((current: number) => {
    setPageState((p) => {
      const _current =
        current < pageFromRef.current
          ? pageFromRef.current
          : current > totalRef.current - 1
          ? totalRef.current - 1
          : current;
      const _direction =
        _current !== p.page
          ? _current < p.page
            ? 'left'
            : 'right'
          : p.direction;
      return { pageSize: p.pageSize, page: _current, direction: _direction };
    });
  }, []);

  const onPageDirection = useCallback((currentDirection: IDirection) => {
    setPageState((p) => ({ ...p, direction: currentDirection }));
  }, []);

  const onCycleChange = useCallback((currentDirection?: IDirection) => {
    setPageState((p) => {
      let _page, _direction: IDirection;
      if ((currentDirection || direction) === 'left') {
        _page = p.page - 1 < 0 ? totalRef.current - 1 : p.page - 1;
        _direction = 'left';
      } else {
        _page =
          p.page + 1 > totalRef.current - 1 ? pageFromRef.current : p.page + 1;
        _direction = 'right';
      }
      return {
        page: _page,
        pageSize: p.pageSize,
        direction: _direction,
      };
    });
  }, []);

  useEffect(() => {
    setPageState((p) => ({ ...p, direction: direction || 'right' }));
  }, [direction]);

  useEffect(() => {
    if (page <= totalRef.current - 1 && page >= pageFromRef.current) {
      setPageState((p) => ({ page, pageSize, direction: p.direction }));
    }
  }, [page, pageSize]);

  return {
    onPrev,
    onNext,
    onChange,
    current: pageState.page,
    pageSize,
    enablePrev: pageState.page > pageFromRef.current,
    enableNext: pageState.page < totalRef.current - 1,
    totalPage,
    pageDirection: pageState.direction,
    onPageDirection,
    onCycleChange,
  };
};
